import { useState } from 'react'
import type { Site, TimeEntry, User } from '@shared/types'
import { del, patch, post } from '../../lib/api'
import { fromLocalInput, toLocalInput } from '../../lib/datetime'
import { Button, ErrorNote, Field, Sheet } from '../../components/ui'

/**
 * The office's one tool for the timesheet: correct an entry, close an entry
 * somebody forgot to clock out of, backfill a shift that was never clocked, or
 * delete an accidental one.
 *
 * Every path requires a reason, and the result shows on the worker's own log as
 * "edited by office" with that reason — a changed timesheet must never be a
 * surprise to the person whose hours it is.
 */

interface EntrySheetProps {
  /** null = create a new entry (backfill). */
  entry: TimeEntry | null
  /** Needed in create mode to pick the person; ignored otherwise. */
  users?: User[]
  sites: Site[]
  timezone: string
  onClose: () => void
  onSaved: (message: string) => Promise<void>
}

export function EntrySheet({ entry, users = [], sites, timezone, onClose, onSaved }: EntrySheetProps) {
  const isNew = entry === null
  const [userId, setUserId] = useState(entry?.userId ?? '')
  const [siteId, setSiteId] = useState(entry?.siteId ?? '')
  const [clockIn, setClockIn] = useState(() =>
    entry ? toLocalInput(entry.clockInAt, timezone) : '')
  const [clockOut, setClockOut] = useState(() =>
    entry?.clockOutAt ? toLocalInput(entry.clockOutAt, timezone) : '')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Deleting hours deserves a second tap, not a browser confirm() that half the
  // crew's phones render off-screen.
  const [armDelete, setArmDelete] = useState(false)

  const save = async () => {
    if (!reason.trim()) {
      setError('Say why — the crew member sees this on their log.')
      return
    }
    if (isNew && (!userId || !siteId || !clockIn)) {
      setError('Pick a person, a site and a clock-in time.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (isNew) {
        await post('/api/admin/time-entries', {
          userId,
          siteId,
          clockInAt: fromLocalInput(clockIn, timezone),
          clockOutAt: clockOut ? fromLocalInput(clockOut, timezone) : null,
          reason: reason.trim(),
        })
        await onSaved('Entry added')
      } else {
        await patch(`/api/admin/time-entries/${entry.id}`, {
          clockInAt: fromLocalInput(clockIn, timezone),
          clockOutAt: clockOut ? fromLocalInput(clockOut, timezone) : null,
          siteId,
          reason: reason.trim(),
        })
        await onSaved('Entry corrected')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save')
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!entry) return
    setBusy(true)
    setError(null)
    try {
      await del(`/api/admin/time-entries/${entry.id}`)
      await onSaved('Entry deleted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'Add a missed entry' : `Correct ${entry.userName ?? 'this'} entry`}
      footer={
        <div className="flex gap-2">
          {!isNew && (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => (armDelete ? void remove() : setArmDelete(true))}
            >
              {armDelete ? 'Really delete' : 'Delete'}
            </Button>
          )}
          <Button variant="primary" className="flex-1" loading={busy} onClick={() => void save()}>
            {isNew ? 'Add entry' : 'Save correction'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {isNew && (
          <Field label="Crew member" required>
            <select className="field" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Choose…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Site" required={isNew}>
          <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {isNew && <option value="">Choose…</option>}
            {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select>
        </Field>
        <Field label="Clock in" required={isNew}>
          <input
            className="field font-mono"
            type="datetime-local"
            value={clockIn}
            onChange={(e) => setClockIn(e.target.value)}
          />
        </Field>
        <Field
          label="Clock out"
          hint={isNew
            ? 'Leave blank to put them on the clock now.'
            : 'Leave blank to reopen the entry.'}
        >
          <input
            className="field font-mono"
            type="datetime-local"
            value={clockOut}
            onChange={(e) => setClockOut(e.target.value)}
          />
        </Field>
        <Field label="Reason" required>
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Forgot to clock out, confirmed 16:30 with the site lead"
          />
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}

